import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Tabs,
  message,
  Spin,
  Button,
  Row,
  Col,
  Tooltip,
  Form
} from 'antd';
import { AssetItemDto, ApprovalWorkflowItemDto } from '@api/Api';
import { useTypedSelector } from '@hooks';
import {
  toggleApprovalAssetModal,
  toggleApprovalModal,
  toggleShareAssetModal
} from '@redux/actions/modalAction';

import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as CheckTaskSvg } from '@assets/icons/check-task.svg';
import { ReactComponent as MagicianSvg } from '@assets/icons/magician-small.svg';
import { ReactComponent as SuccessSvg } from '@assets/icons/success.svg';
import { ReactComponent as CopySvg } from '@assets/icons/copy-secondary.svg';
import { ReactComponent as SelectMenuItemSvg } from '@assets/icons/select-menu-item.svg';

import { useAuth } from '@hooks/useAuth';
import { useCurrentWorkspace } from '@hooks/workspace';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import { useOrganization } from '@components/OrganizationBoundary';
import { setAsset } from '@redux/actions/mediaViewerAction';
import { nonNull } from '@helpers/non-null';

import {
  useCreateAssetReviewMutation,
  useAssetReviewQuery,
  useAssetReviewContentQuery,
  useShareAssetReviewLinkMutation,
  useUpdateAssetReviewMutation
} from '@hooks/queries/assetReview';
import { ShareAssetModalStateProps } from '@redux/types/modalType';
import type { SelectedItem } from '@components/InviteMembersSelect/InviteMembersSelect';

import Sharing from './Sharing';
import Settings from './Settings';

import './ShareAssetModal.less';

const { TabPane } = Tabs;

export type FormValues = {
  name: string;
  userList: SelectedItem[];
  showMessage: boolean;
  customMessage: string;
  allowComments: boolean;
  allowDownloads: boolean;
  allowAllVersions: boolean;
  allowAllComments: boolean;
  password: string | null;
  addPassword: boolean;
  includePrivateFolders: boolean;
};

type ShareAssetModalProps = ShareAssetModalStateProps;

function ShareAssetModal({
  visible,
  reviewId,
  defaultActiveTab,
  campaignId,
  assets: initialAssets,
  folders: initialFolders,
  defaultName,
  isWholeCampaign
}: ShareAssetModalProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth(true, 'internal');
  const [form] = Form.useForm();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const { currentOrganization } = useOrganization(false);
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );

  const { data: assetReview, isLoading: isLoadingAssetReview } =
    useAssetReviewQuery({
      params: {
        reviewId: reviewId || ''
      },
      enabled: !!reviewId && !!visible
    });

  const { data: assetReviewContent, isLoading: isLoadingAssetReviewContent } =
    useAssetReviewContentQuery({
      params: {
        reviewId: reviewId || ''
      },
      enabled: !!reviewId && !!visible
    });

  const isLoading =
    (isLoadingAssetReview || isLoadingAssetReviewContent) && !!reviewId;

  const assets = assetReviewContent?.assets || [];

  const { mutateAsync: createAssetReview, isPending: isCreatingAssetReview } =
    useCreateAssetReviewMutation({});
  const { mutateAsync: updateAssetReview, isPending: isUpdatingAssetReview } =
    useUpdateAssetReviewMutation({});
  const {
    mutateAsync: shareAssetReviewLink,
    isPending: isSharingAssetReviewLink
  } = useShareAssetReviewLinkMutation({});

  const linkCopiedTimeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

  const assetReviewId = assetReview?.review?.id;
  const isSubmitting =
    isCreatingAssetReview || isUpdatingAssetReview || isSharingAssetReviewLink;

  const allowCommentsWatch = Form.useWatch('allowComments', form);
  const allowDownloadsWatch = Form.useWatch('allowDownloads', form);
  const userListWatch = Form.useWatch('userList', form);
  const passwordWatch = Form.useWatch('password', form);

  const [activeTab, setActiveTab] = useState<string>(
    defaultActiveTab || 'sharing'
  );
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const skipCloseAfterNextSubmitRef = useRef(false);

  const canCreateAw = !!currentWorkspace?.permissions.createApprovalWorkflows;
  const previewLink: string | null = useMemo(() => {
    if (!assetReviewId) return null;
    return `/external-reviewer?reviewId=${assetReviewId}&password=${
      passwordWatch ? 'true' : 'false'
    }&active=true&preview=true`;
  }, [assetReviewId, passwordWatch]);
  const isLastVersionShared = useMemo(() => {
    const asset = assets?.[0];
    const v = asset?.asset?.versions.find(
      (it) => it.id === asset?.asset?.versions[0].id
    );
    const index = asset?.asset?.versions.findIndex((it) => it.id === v?.id);
    return index === 0;
  }, [assets]);

  const bottomText: string = useMemo(() => {
    if (allowCommentsWatch && allowDownloadsWatch) {
      return 'This link lets anyone download and comment on the media.';
    }
    if (allowCommentsWatch) {
      return 'This link lets anyone comment on the media but not download.';
    }
    if (allowDownloadsWatch) {
      return 'This link lets anyone download the media but not comment.';
    }
    return 'This link lets anyone view the media but not download or comment.';
  }, [allowCommentsWatch, allowDownloadsWatch]);

  const callbackAwModal = (
    aw: ApprovalWorkflowItemDto | null,
    asset: AssetItemDto
  ) => {
    const updateAsset = {
      ...asset,
      asset: {
        ...nonNull(asset.asset),
        approvalWorkflow: aw
          ? {
              id: aw.id,
              status: aw.status,
              isPaused: aw.isPaused,
              isFinished: aw.isFinished,
              permissions: {
                manage: aw.permissions.manage,
                read: asset.asset?.approvalWorkflow?.permissions.read ?? true
              }
            }
          : null
      }
    };
    dispatch(setAsset(updateAsset));
  };

  const handleOpenApprovalModal = () => {
    const asset = assets?.[0];
    const v = asset?.asset?.versions.find(
      (it) => it.id === asset?.asset?.versions[0].id
    );

    const _assetVersion = selectedVersion || v;
    if (_assetVersion && asset) {
      onCloseModal();
      if (asset.asset?.approvalWorkflow) {
        dispatch(
          toggleApprovalAssetModal({
            assetId: asset.id,
            visible: true,
            versionId: _assetVersion.id,
            approvalWorkflowId: asset.asset?.approvalWorkflow.id,
            callback: (aw) => callbackAwModal(aw, asset)
          })
        );
        return;
      }
      dispatch(
        toggleApprovalModal({
          visible: true,
          approvalWorkflowId: asset?.asset?.approvalWorkflow?.id,
          assetVersions: [
            {
              ..._assetVersion,
              assetId: asset.id
            }
          ],
          permissions: {
            create: canCreateAw
          },
          campaignId: asset.campaignId,
          callback: (aw) => callbackAwModal(aw, asset)
        })
      );
    }
  };

  const onSubmitInviteUser = async (data: {
    users: string[];
    groups: string[];
    message?: string;
    assetReviewId: string;
  }): Promise<boolean | void> => {
    try {
      await shareAssetReviewLink({
        assetReviewId: data.assetReviewId,
        userEmails: data.users,
        groupIds: data.groups,
        ...(data.message && { customMessage: data.message })
      });
      setIsSuccess(true);
      MixpanelService.track(user.id, MixpanelEventType.MANAGE_ASSETS, {
        addToFavorite: false,
        deleteAsset: false,
        newAsset: false,
        externalLinkShare: true,
        approvalRouting: false,
        newCommentAsset: false,
        annotationLeft: false,
        taskFromComment: false
      });
      return true;
    } catch (error: any) {
      message.error({
        content: error?.response?.data?.message || 'Something went wrong',
        className: 'message-dark-modal'
      });
      return false;
    }
  };

  const onChangeTab = (key: string) => {
    setActiveTab(key);
  };

  const onCloseModal = () => {
    dispatch(toggleShareAssetModal({ visible: false }));
  };

  const showShareSuccessToast = ({
    isCopied = false,
    name,
    url
  }: {
    isCopied?: boolean;
    name: string;
    url: string;
  }) => {
    const messageKey = 'share-asset-modal-success';
    message.success({
      key: messageKey,
      className: 'message-dark-modal share-asset-success-toast',
      content: (
        <div className="message-dark-modal-content">
          <div className="message-dark-modal-inner">
            {assetReviewId
              ? `Link “${name}” successfully updated`
              : `Link “${name}” successfully created`}
          </div>
          <Button
            icon={
              isCopied ? (
                <SelectMenuItemSvg className="b-icon-select" />
              ) : (
                <CopySvg className="b-icon-copy" />
              )
            }
            onClick={() => {
              console.log(url, 'url');
              navigator.clipboard.writeText(url);
              showShareSuccessToast({ isCopied: true, name, url });
              if (linkCopiedTimeoutIdRef.current)
                clearTimeout(linkCopiedTimeoutIdRef.current);
              linkCopiedTimeoutIdRef.current = setTimeout(() => {
                showShareSuccessToast({ isCopied: false, name, url });
              }, 2000);
            }}
            type="primary"
          >
            Copy link
          </Button>
          <CrossSvg
            onClick={() => message.destroy(messageKey)}
            className="message-dark-modal-close-btn"
          />
        </div>
      ),
      icon: <SuccessSvg className="anticon" />,
      duration: 3,
      onClose: () => {
        if (linkCopiedTimeoutIdRef.current)
          clearTimeout(linkCopiedTimeoutIdRef.current);
      }
    });
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const {
        allowComments,
        allowDownloads,
        allowAllVersions,
        allowAllComments,
        name,
        password,
        includePrivateFolders,
        userList,
        customMessage
      } = data;

      const { users, groups } = userList.reduce(
        (acc, item) => {
          if (item.type === 'group') acc.groups.push(item.id);
          if (item.type === 'new' || item.type === 'member')
            acc.users.push(item.email);
          return acc;
        },
        { users: [] as string[], groups: [] as string[] }
      );

      const hasUsersOrGroups = !!users?.length || !!groups?.length;
      let reviewId: string | null = null;
      let reviewUrl: string | null = null;

      if (assetReviewId) {
        reviewId = assetReviewId;
        reviewUrl = assetReview.review.url;
        await updateAssetReview({
          reviewId: assetReviewId,
          name,
          allowComments,
          allowDownloads,
          allowAllVersions,
          allowAllComments,
          password: password || null
        });
      } else {
        const res = await createAssetReview({
          campaignId: campaignId || '',
          name,
          allowComments,
          allowDownloads,
          allowAllVersions,
          allowAllComments,
          password: password || null,
          assets: initialAssets || [],
          folders: initialFolders || [],
          includePrivateFolders,
          isWholeCampaign
        });
        reviewId = res.data.review.id;
        reviewUrl = res.data.review.url;
      }
      if (hasUsersOrGroups) {
        await onSubmitInviteUser({
          users,
          groups,
          message: customMessage,
          assetReviewId: reviewId
        });
        return;
      }
      showShareSuccessToast({ name, url: reviewUrl || '' });
      const skipClose = skipCloseAfterNextSubmitRef.current;
      skipCloseAfterNextSubmitRef.current = false;
      if (!skipClose) {
        onCloseModal();
        if (campaignId && !assetReviewId) {
          navigate(`/campaigns/single/${campaignId}?tab=sharedLinks`);
        }
      }
    } catch (error: any) {
      skipCloseAfterNextSubmitRef.current = false;
      if (error?.response?.data?.errorCode === 'feature_not_allowed') {
        message.error({
          content: 'This feature is included in the higher plan',
          className: 'message-dark-modal'
        });
      } else {
        message.error({
          content: 'Something went wrong',
          className: 'message-dark-modal'
        });
      }
      throw error;
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      name: assetReview?.review?.name ?? defaultName ?? '',
      userList: [],
      customMessage: '',
      allowComments: assetReview?.review?.allowComments ?? true,
      allowDownloads: assetReview?.review?.allowDownloads ?? true,
      allowAllVersions: assetReview?.review?.allowAllVersions ?? false,
      allowAllComments: assetReview?.review?.allowAllComments ?? true,
      password: assetReview?.review?.password ?? null,
      addPassword: !!assetReview?.review?.password,
      includePrivateFolders: assetReview?.review?.includePrivateFolders || false
    });
  }, [assetReview?.review, form, defaultName, visible]);

  return (
    <Modal
      open={visible}
      footer={null}
      centered={true}
      destroyOnClose
      closeIcon={<CrossSvg />}
      width={492}
      zIndex={1006}
      className="share_asset_modal_container"
      onCancel={onCloseModal}
      afterClose={() => dispatch(toggleShareAssetModal(null))}
    >
      {isLoading ? (
        <Spin className="player_loader share_asset_modal_loader" />
      ) : isSuccess ? (
        <div className="modal_container">
          <h2 className="modal_container__title">Link successfully shared</h2>
          <div className="share_asset_modal_success">
            <div className="share_asset_modal_success-inner">
              <CheckTaskSvg className="share_asset_modal_success-icon" />
            </div>
          </div>
        </div>
      ) : (
        <div className="modal_container">
          <h2 className="modal_container__title">
            {(assets?.length || 0) > 1
              ? `Share ${assets?.length} media`
              : 'Share media'}
          </h2>
          <div className="share_asset_modal_link">
            {visible &&
              assets.slice(0, 2).map((it, index) => {
                let v = it.asset?.versions.find(
                  (version) => version.id === it.asset?.versions[0].id
                );
                if (!v) v = it.asset?.versions[0];
                const isLastItem = assets.slice(0, 2).length - 1 === index;
                return (
                  <span key={v?.id}>
                    {v?.name}.{v?.extension}
                    {!isLastItem && ','}
                  </span>
                );
              })}
            {visible && assets.slice(2).length > 0 && (
              <Tooltip
                title={
                  <>
                    {assets.slice(2).map((it) => {
                      let v = it.asset?.versions.find(
                        (version) => version.id === it.asset?.versions[0].id
                      );
                      if (!v) v = it.asset?.versions[0];
                      if (!v) return null;
                      return (
                        <div key={v.id}>
                          {v.name}.{v.extension}
                        </div>
                      );
                    })}
                  </>
                }
                placement="bottom"
                overlayClassName="toggle_favorite_overlay toggle_favorite_overlay"
              >
                <span className="more">{assets.slice(2).length}+</span>
              </Tooltip>
            )}
          </div>

          <Form
            form={form}
            onFinish={onSubmit}
            onFinishFailed={() => setActiveTab('sharing')}
            className="share_asset_modal_form"
          >
            <Tabs
              activeKey={activeTab}
              onTabClick={onChangeTab}
              destroyInactiveTabPane={false}
              className="modal_container__invitation_type"
            >
              <TabPane tab="Sharing" key="sharing" forceRender={true}>
                <Sharing
                  link={assetReview?.review?.url || ''}
                  previewLink={previewLink}
                  campaignId={campaignId || ''}
                  form={form}
                />
              </TabPane>
              <TabPane tab="Settings" key="settings" forceRender={true}>
                <Settings
                  form={form}
                  isWholeCampaign={isWholeCampaign || false}
                  onCloseModal={onCloseModal}
                  isSubmitting={isSubmitting}
                  markSkipCloseAfterNextSubmit={() => {
                    skipCloseAfterNextSubmitRef.current = true;
                  }}
                />
              </TabPane>
            </Tabs>

            <Row justify="end" gutter={20}>
              <Col>
                <Button
                  type="ghost"
                  size="large"
                  style={{
                    border: 'none',
                    height: 48,
                    padding: '8px 15px'
                  }}
                  onClick={onCloseModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  style={{ width: 152, height: 48, padding: '8px 15px' }}
                  loading={isSubmitting}
                >
                  {userListWatch?.length ? 'Send' : 'Done'}
                </Button>
              </Col>
            </Row>

            {activeTab === 'sharing' && !!assetReview?.review?.id && (
              <div className="share_asset_modal_info">
                {currentOrganization?.entity?.features.approvalRouting &&
                  isLastVersionShared &&
                  assets?.length === 1 && (
                    <>
                      <p className="modal_container__copy">
                        Route this media for approval
                      </p>
                      <div className="share_asset_modal_info-text">
                        Did you know you can collect approvals on this media? We
                        started an approval routing for you based on your
                        previous shares.
                      </div>
                      <Button
                        type="primary"
                        htmlType="button"
                        onClick={handleOpenApprovalModal}
                      >
                        Complete approval routing
                      </Button>
                    </>
                  )}
              </div>
            )}
            {activeTab === 'sharing' && !assetReview?.review?.id && (
              <div className="share_asset_modal_bottom-info">
                <Row gutter={16}>
                  <Col className="share_asset_modal_bottom-info-icon">
                    <MagicianSvg />
                  </Col>
                  <Col className="share_asset_modal_bottom-info-text">
                    {bottomText} Manage permissions{' '}
                    <a
                      href="#!"
                      onClick={(e) => {
                        e.preventDefault();
                        onChangeTab('settings');
                      }}
                    >
                      here.
                    </a>
                  </Col>
                </Row>
              </div>
            )}
            {activeTab === 'settings' &&
              isLastVersionShared &&
              assets?.length === 1 &&
              currentOrganization?.entity?.features.approvalRouting && (
                <div className="share_asset_modal_bottom-info">
                  <Row gutter={16}>
                    <Col className="share_asset_modal_bottom-info-icon">
                      <MagicianSvg />
                    </Col>
                    <Col className="share_asset_modal_bottom-info-text">
                      Looking to route the media through stakeholder approvals?
                      Try it{' '}
                      <a
                        href="#!"
                        onClick={(e) => {
                          e.preventDefault();
                          handleOpenApprovalModal();
                        }}
                      >
                        here.
                      </a>
                    </Col>
                  </Row>
                </div>
              )}
          </Form>
        </div>
      )}
    </Modal>
  );
}

export default ShareAssetModal;
